// Single source of truth for the Gemini model used across the app.
// Persisted in localStorage so admins can switch without a PocketBase migration.

import toast from 'react-hot-toast';
import { recordAttempt, rankModels, notifyAttemptStart } from './geminiAttemptLog';

const STORAGE_KEY = 'hr_dashboard.gemini_model';
const FALLBACK_MODEL = 'gemini-3.8-flash';

// Curated subset of the models this app is allowed to call. Order matters:
// recommended first, and the first entry is FALLBACK_MODEL.
export const SUPPORTED_GEMINI_MODELS = [
  {
    id: 'gemini-3.8-flash',
    label: 'Gemini 3.8 Flash',
    hint: 'Empfohlen — neuestes und stärkstes Flash-Modell'
  },
  {
    id: 'gemini-3.7-flash',
    label: 'Gemini 3.7 Flash',
    hint: 'Vorgängergeneration, stark bei komplexen Aufgaben'
  },
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    hint: 'Bisherige Standardwahl'
  },
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    hint: 'Schneller und günstiger, schwächer im Detail'
  },
  {
    id: 'gemini-3-flash-preview',
    label: 'Gemini 3 Flash (Preview)',
    hint: 'Vorschauversion — kann sich ändern'
  },
  {
    id: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    hint: 'Starkes Reasoning, langsamer — nur mit aktivem Billing'
  },
  {
    id: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash',
    hint: 'Ältere Generation, weiterhin verfügbar'
  },
  {
    id: 'gemini-2.5-flash-lite',
    label: 'Gemini 2.5 Flash-Lite',
    hint: 'Günstigste 2.5-Variante'
  }
];

const isSupported = (model) =>
  SUPPORTED_GEMINI_MODELS.some((m) => m.id === model);

export const getActiveGeminiModel = () => {
  try {
    const stored = typeof window !== 'undefined'
      ? window.localStorage?.getItem(STORAGE_KEY)
      : null;
    const trimmed = String(stored || '').trim();
    if (trimmed) return trimmed;
  } catch (e) {
    // localStorage may be unavailable (SSR, privacy mode); fall through.
  }
  return FALLBACK_MODEL;
};

export const setActiveGeminiModel = (model) => {
  const next = String(model || '').trim() || FALLBACK_MODEL;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  } catch (e) {
    // Ignore — caller already sees the in-memory return.
  }
  return next;
};

// The key must never reach the browser. A bundled key is world-readable, and
// Google suspends keys it finds in public bundles — which is exactly what
// happened here. Requests go to /gemini-api instead; the key is attached by a
// proxy in front: src/setupProxy.js in dev, Nginx on hr.eihub.de in production.
// See docs/gemini-proxy.md.
const GEMINI_API_BASE = String(process.env.REACT_APP_GEMINI_API_URL || '/gemini-api')
  .replace(/\/+$/, '');

export const buildGeminiApiUrl = (modelOverride) => {
  const model = (modelOverride && String(modelOverride).trim()) || getActiveGeminiModel();
  return `${GEMINI_API_BASE}/${model}:generateContent`;
};

// Wie lange ein einzelner Versuch laufen darf. Bewusst kuerzer als das
// proxy_read_timeout der Instanz davor (120 s, siehe docs/gemini-proxy.md): so
// entscheidet dieser Code ueber den Abbruch und nicht der Proxy. Andersherum
// kam ein Proxy-Timeout als "504 - Google-Backend antwortet nicht" an, obwohl
// Google nie gefragt wurde.
const ATTEMPT_TIMEOUT_MS = 75000;

// Fehler, bei denen ein anderes Modell nichts aendert: Schluessel und Payload
// bleiben dieselben. Hier bricht die Kette ab, statt denselben Fehler acht Mal
// zu erzeugen. 404 gehoert bewusst NICHT dazu — ein nicht existierender
// Modellname ist modellspezifisch, da geht es mit dem naechsten weiter.
const FATAL_STATUS = new Set([400, 401, 403]);

// Der groesste Latenzhebel. Ohne ihn laeuft ein Thinking-Modell bei einer
// vollen Bewertung leicht ueber jede Deadline — und eine Kette, die alle
// Modelle durchprobiert, braucht kurze Versuche, sonst dauert sie Minuten.
// Ein Aufrufer, der thinkingConfig selbst setzt, behaelt das letzte Wort.
const withThinkingConfig = (payload, model) => {
  const generationConfig = payload?.generationConfig;
  if (generationConfig?.thinkingConfig) return payload;

  const id = String(model);
  let thinkingConfig = null;
  if (id.startsWith('gemini-3')) {
    thinkingConfig = { thinkingLevel: 'low' };
  } else if (id.startsWith('gemini-2.5') && !id.includes('pro')) {
    // 2.5 kennt thinkingLevel nicht, nur ein Budget. Pro laesst sich nicht auf
    // 0 setzen und wird deshalb ausgelassen.
    thinkingConfig = { thinkingBudget: 0 };
  }
  if (!thinkingConfig) return payload;

  return { ...payload, generationConfig: { ...(generationConfig || {}), thinkingConfig } };
};

// Das gewuenschte Modell zuerst, danach alle uebrigen in der Reihenfolge, die
// das Tagesprotokoll nahelegt. Ausgeschlossen wird nie eines: ein Kontingent
// kann sich im Lauf des Tages erholen.
export const buildGeminiCandidates = (preferred) => {
  const rest = SUPPORTED_GEMINI_MODELS.map((m) => m.id).filter((id) => id !== preferred);
  return [preferred, ...rankModels(rest)];
};

// Ein Wechsel wird nur dauerhaft, wenn er die freie Modellwahl betrifft. Ein
// Schritt mit explizitem Modell darf die Einstellung des Admins nicht
// ueberschreiben. Sichtbar ist er immer — ein stillschweigender Downgrade
// waere als Qualitaetsverlust nicht bemerkbar.
const announceSwitch = (preferred, used, explicitModel) => {
  if (used === preferred) return;
  if (!explicitModel) setActiveGeminiModel(used);
  toast(`${preferred} antwortete nicht — es lief auf ${used}.`, {
    id: 'gemini-model-fallback',
    icon: '\u26a0\ufe0f',
    duration: 6000
  });
};

// Der Verlauf eines Durchlaufs, angeheftet an die Antwort. Ohne ihn zeigt die
// Oberflaeche nur den letzten Fehler — ein Durchlauf ueber acht Modelle sieht
// dann aus, als haette gar kein Fallback stattgefunden.
const ATTEMPTS_KEY = '__geminiAttempts';

const attachAttempts = (target, attempts) => {
  try {
    Object.defineProperty(target, ATTEMPTS_KEY, { value: attempts, enumerable: false });
  } catch (e) {
    // Eingefrorenes Objekt — der Verlauf steht weiterhin im Tagesprotokoll.
  }
  return target;
};

/** Der Verlauf zu einer Antwort oder einem Fehler, oder [] wenn keiner dranhaengt. */
export const getGeminiAttempts = (target) => (target && target[ATTEMPTS_KEY]) || [];

/** Ein Verlauf als eine Zeile: "gemini-3.8-flash 503, gemini-3.7-flash 429". */
export const describeGeminiAttempts = (attempts = []) =>
  attempts.map((a) => `${a.model} ${a.status ?? a.reason}`).join(', ');

/**
 * Schickt die Anfrage an Gemini und probiert dabei so lange weitere Modelle,
 * bis eines antwortet. Jeder Versuch landet im Tagesprotokoll
 * (siehe geminiAttemptLog.js), das die Reihenfolge des naechsten Aufrufs
 * beeinflusst.
 *
 * Gibt die erste erfolgreiche Response zurueck. Scheitert alles, kommt die
 * letzte Fehler-Response zurueck, damit die bestehende Fehlerbehandlung der
 * Aufrufer unveraendert greift.
 */
export const geminiFetch = async ({ payload, signal, model: explicitModel } = {}) => {
  const preferred = (explicitModel && String(explicitModel).trim()) || getActiveGeminiModel();
  const candidates = buildGeminiCandidates(preferred);

  let lastResponse = null;
  let lastError = null;
  const attempts = [];

  for (const model of candidates) {
    if (signal?.aborted) throw signal.reason ?? new Error('Abgebrochen');

    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, ATTEMPT_TIMEOUT_MS);
    const relayAbort = () => controller.abort();
    if (signal) signal.addEventListener('abort', relayAbort);

    notifyAttemptStart(model);

    try {
      const response = await fetch(buildGeminiApiUrl(model), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(withThinkingConfig(payload, model)),
        signal: controller.signal
      });

      recordAttempt(model, { status: response.status, ok: response.ok });
      attempts.push({ model, status: response.status, ok: response.ok });

      if (response.ok) {
        announceSwitch(preferred, model, explicitModel);
        return attachAttempts(response, attempts);
      }
      if (FATAL_STATUS.has(response.status)) return attachAttempts(response, attempts);
      lastResponse = response;
    } catch (err) {
      // Ein Abbruch durch den Aufrufer ist keine Modellschwaeche.
      if (signal?.aborted) throw err;
      const reason = timedOut ? 'timeout' : 'netzwerk';
      recordAttempt(model, { status: null, reason });
      attempts.push({ model, status: null, reason, ok: false });
      lastError = err;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', relayAbort);
    }
  }

  toast.error(`Kein Modell hat geantwortet. Versucht: ${describeGeminiAttempts(attempts)}`, {
    id: 'gemini-all-models-failed',
    duration: 8000
  });

  if (lastResponse) return attachAttempts(lastResponse, attempts);
  throw attachAttempts(lastError || new Error('Kein Gemini-Modell hat geantwortet.'), attempts);
};

export const FALLBACK_GEMINI_MODEL = FALLBACK_MODEL;
export const isSupportedGeminiModel = isSupported;
